// Flags a saved Shop Insurance policy whose quote was priced for client details
// that no longer hold, and keeps it out of the plan totals until it is re-quoted.
//
// A saved policy keeps the quote row it was added from. When the client's date
// of birth, sex or tobacco answers change later (or the partner's, for a joint
// policy), nothing re-quotes it, yet every derivative pass recalculates its ROI
// against the current details. A quote priced for a 65-year-old then gets
// modelled on a 40-year-old and reports thousands of percent.
//
// How it works:
//   - Only policies added from Shop Insurance or guided planning are checked.
//     Policies the client owns (uploaded or typed in) are never flagged.
//   - Age is compared as of the day the policy was added (`policyAddedAt`),
//     using the current date of birth, so birthdays since then never flag a
//     policy but an edited date of birth does. Policies saved without that date
//     are compared with the current age, allowing one year either way.
//   - Sex and tobacco follow `calcClientDemographics`, which Shop Insurance uses
//     to fetch quotes. Tobacco is only compared where the quote records it:
//     Lincoln stores 'None', and CareMatters Annuity keeps its risk class there.
//   - Joint quotes match the couple in either order, like the quote search. If
//     the partner is not loaded, the client only has to match one of the two.
//   - State is not compared: a policy keeps its issued state when the client
//     moves, and one quote row is sold in many states.
//
// Nothing is stored. `quoteOutdatedReason` is recomputed on every pass, so
// re-quoting, removing the policy or correcting the details clears it.
//
// It is set before anything prices the plan (care-cost discounts included), and
// `isPolicyCountedInPlan` is the one rule every total reads: benefit, combined
// ROI, the care-cost discount a policy brings, and the premium it needs funded.

import { FundingPolicyType } from '../../appModel';
import { isTobacco } from '../../utils/calcClientTobaccoStatus';
import { calculateAge } from '../survey/calcSurveyDerivatives';

type Sex = 'male' | 'female';
type TobaccoStatus = 'Tobacco' | 'Non-Tobacco';

interface Insured {
  age: number;
  sex: Sex | null;
  tobacco: TobaccoStatus | null;
}

interface ClientInsured {
  ageWhenAdded: number;
  ageNow: number;
  sex: Sex;
  tobacco: TobaccoStatus;
}

type SavedQuote = PolicyQuote | TraditionalLTCQuote | ShortTermCareQuote;

const TOBACCO_AGNOSTIC_POLICY_TYPES = new Set<number>([
  FundingPolicyType.careMattersAnnuity,
  FundingPolicyType.jointCareMattersAnnuity,
]);

function parseSavedQuote(value: unknown): SavedQuote | null {
  if (!value) return null;
  if (typeof value === 'object') return value as SavedQuote;
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value) as SavedQuote;
  } catch {
    return null;
  }
}

function toSex(value: unknown): Sex | null {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (normalized.startsWith('m')) return 'male';
  if (normalized.startsWith('f')) return 'female';
  return null;
}

function toTobaccoStatus(value: unknown): TobaccoStatus | null {
  return value === 'Tobacco' || value === 'Non-Tobacco' ? value : null;
}

function insuredOnQuote(quote: SavedQuote): Insured[] {
  if ('shortTermCareQuoteId' in quote) {
    const tobacco =
      typeof quote.tobacco === 'boolean' ? (quote.tobacco ? 'Tobacco' : 'Non-Tobacco') : null;
    return [{ age: quote.issueAge, sex: toSex(quote.gender), tobacco }];
  }

  if ('traditionalLTCQuoteId' in quote) {
    return [{ age: quote.primaryAge, sex: toSex(quote.primaryGender), tobacco: null }];
  }

  const recordsTobacco = !TOBACCO_AGNOSTIC_POLICY_TYPES.has(quote.fundingPolicyType);
  const insured: Insured[] = [
    {
      age: quote.primaryAge,
      sex: toSex(quote.primaryGender),
      tobacco: recordsTobacco ? toTobaccoStatus(quote.primaryTobaccoStatus) : null,
    },
  ];
  if (quote.policyType === 'Joint Life' && typeof quote.secondaryAge === 'number') {
    insured.push({
      age: quote.secondaryAge,
      sex: toSex(quote.secondaryGender),
      tobacco: recordsTobacco ? toTobaccoStatus(quote.secondaryTobaccoStatus) : null,
    });
  }
  return insured;
}

// Stored as text in FundingSourceDetails, so it can arrive as a numeric string.
function toAddedAt(value: unknown): Date | null {
  const milliseconds = typeof value === 'string' ? Number(value) : value;
  return typeof milliseconds === 'number' && Number.isFinite(milliseconds) && milliseconds > 0
    ? new Date(milliseconds)
    : null;
}

function insuredFromIntake(
  intakeSurvey: Partial<IntakeSurvey> | undefined,
  addedAt: Date | null,
): ClientInsured | null {
  if (!intakeSurvey?.clientBirthDate) return null;
  const birthDate = new Date(intakeSurvey.clientBirthDate);
  if (Number.isNaN(birthDate.getTime())) return null;

  const ageNow = calculateAge(birthDate);
  return {
    ageNow,
    ageWhenAdded: addedAt ? calculateAge(birthDate, addedAt) : ageNow,
    sex: intakeSurvey.clientGenderAtBirth === 'Male' ? 'male' : 'female',
    tobacco: isTobacco(intakeSurvey) ? 'Tobacco' : 'Non-Tobacco',
  };
}

function matches(quoted: Insured, actual: ClientInsured, ageTolerance: number): boolean {
  return (
    Math.abs(quoted.age - actual.ageWhenAdded) <= ageTolerance &&
    (quoted.sex === null || quoted.sex === actual.sex) &&
    (quoted.tobacco === null || quoted.tobacco === actual.tobacco)
  );
}

// Only the details that changed, so the reason stays one short sentence.
function describeChange(quoted: Insured, actual: ClientInsured, ageTolerance: number): string {
  const was: string[] = [];
  const now: string[] = [];
  if (Math.abs(quoted.age - actual.ageWhenAdded) > ageTolerance) {
    was.push(`age ${quoted.age}`);
    now.push(`age is now set to ${actual.ageNow}`);
  }
  if (quoted.sex && quoted.sex !== actual.sex) {
    was.push(quoted.sex);
    now.push(`sex is now set to ${actual.sex}`);
  }
  if (quoted.tobacco && quoted.tobacco !== actual.tobacco) {
    was.push(quoted.tobacco.toLowerCase());
    now.push(`tobacco status is now set to ${actual.tobacco.toLowerCase()}`);
  }
  const changes =
    now.length > 1 ? `${now.slice(0, -1).join(', ')} and ${now[now.length - 1]}` : now[0];
  return `This quote was priced for ${was.join(', ')}, but the client's ${changes}.`;
}

function describePerson(age: number, sex: Sex | null): string {
  return sex ? `age ${age} (${sex})` : `age ${age}`;
}

function describeCoupleChange(
  quoted: [Insured, Insured],
  actual: ClientInsured[],
  subject: string,
): string {
  const showSex = quoted.some(insured => insured.sex);
  const quotedPeople = quoted.map(insured => describePerson(insured.age, insured.sex));
  const now = actual
    .map(insured => describePerson(insured.ageNow, showSex ? insured.sex : null))
    .join(' and ');
  // Age and sex still line up (in either order), so tobacco is what changed.
  const whenAdded = actual.map(insured =>
    describePerson(insured.ageWhenAdded, showSex ? insured.sex : null),
  );
  const tobaccoOnly =
    whenAdded.length === quotedPeople.length &&
    [...whenAdded].sort().join() === [...quotedPeople].sort().join();
  return (
    `This joint quote was priced for ${quotedPeople.join(' and ')}, but ${subject} ${now}.` +
    (tobaccoOnly ? ' The tobacco status has changed.' : '')
  );
}

/**
 * Why a saved policy's quote no longer fits the client, or undefined when it
 * still does (or the policy is not a saved quote).
 */
export function findOutdatedQuoteReason(
  policy: PolicyFundingSource,
  client: { intakeSurvey?: Partial<IntakeSurvey> },
  partner?: { intakeSurvey?: Partial<IntakeSurvey> } | null,
): string | undefined {
  // Stored as text, so the server hands it over as 'true' or 'false'.
  const addedViaShopInsurance: unknown = policy.addedViaShopInsurance;
  if (addedViaShopInsurance !== true && addedViaShopInsurance !== 'true') return undefined;
  const quote = parseSavedQuote(policy.sourceQuote);
  if (!quote) return undefined;

  const addedAt = toAddedAt(policy.policyAddedAt);
  const quoted = insuredOnQuote(quote).filter(insured => typeof insured.age === 'number');
  const self = insuredFromIntake(client.intakeSurvey, addedAt);
  if (quoted.length === 0 || !self) return undefined;

  const ageTolerance = addedAt ? 0 : 1;

  if (quoted.length === 1) {
    const [only] = quoted;
    return matches(only, self, ageTolerance) ? undefined : describeChange(only, self, ageTolerance);
  }

  const [first, second] = quoted;
  const other = insuredFromIntake(partner?.intakeSurvey, addedAt);
  if (!other) {
    return matches(first, self, ageTolerance) || matches(second, self, ageTolerance)
      ? undefined
      : describeCoupleChange([first, second], [self], 'the client is now');
  }

  const coupleMatches =
    (matches(first, self, ageTolerance) && matches(second, other, ageTolerance)) ||
    (matches(first, other, ageTolerance) && matches(second, self, ageTolerance));
  return coupleMatches
    ? undefined
    : describeCoupleChange([first, second], [self, other], 'the couple is now');
}

/** Whether a policy counts toward the plan's totals and combined ROI. */
export function isPolicyCountedInPlan(policy: PolicyFundingSource): boolean {
  return !policy.isDeactivated && !policy.quoteOutdatedReason;
}

/** Recomputes `quoteOutdatedReason` on every saved policy of the client. */
export function markOutdatedSavedQuotes(
  mutableClient: Pick<Client, 'intakeSurvey' | 'multipleFundingSources' | 'mutableClientPartner'>,
): void {
  const policies = mutableClient.multipleFundingSources?.ltcPolicy ?? {};
  Object.values(policies).forEach(policy => {
    policy.quoteOutdatedReason = findOutdatedQuoteReason(
      policy,
      mutableClient,
      mutableClient.mutableClientPartner,
    );
  });
}
